import { useLazyQuery, useMutation, useQuery } from "@apollo/client/react";
import { useEffect, useMemo, useState } from "react";

import { Grid } from "@mui/material";
import toast from "react-hot-toast";
import { RenderInputByTypeMobile } from "../../../../../../components/formGroupGenerator/RenderInputByTypeMobile";
import { useVisita } from "../../../../../../context/VisitaContext";
import SAVE_VISITA_OTROS_PROFESIONALES, { adaptAddVisitaOtrosProfesionalesInput } from "../../../../../../data/graphQL/mutations/Visitas/saveVisitaOtrosProfesionales";
import GET_COMBO_VISITA_VALOR, { adaptCombosVisitas } from "../../../../../../data/graphQL/queries/MisVisitas/getCombosVisitaValor";
import { GET_INFORMACION_GENERAL, parseInformacionGeneral } from "../../../../../../data/graphQL/queries/MisVisitas/getInfoGeneral";
import { obtenerCoordenadas } from "../../../../constants/getUbicacion";
import { idTiposProfesionales } from "../../../../constants/tiposProfesionales";
import { useStepSave } from "../../../../hooks/useStepSave";
import { filterVisitaData, getInputNames } from "../../../../utils/formDataHelper";
import { validateStepFields } from "../../../../utils/visitaStepValidation";
import { ID_TIPO_VISITA_PRESENCIAL } from "../../../MisVisitasCreationContent";
import CardBox from "../../CardBox/CardBox";
import VisitaFirmaInput from "../../VisitaFirmaInput/VisitaFirmaInput";

const ID_COMBO_TIPO_VISITA = 1;
const COMBO_VISITA_INERLOCUTOR = 'Interlocutor';

export default function InformacionPaciente({
  combosVisita,
  idPaciente,
  idEvaluacion,
  idProfesional,
  idFrecuencia,
  idTipoProfesional,
  isClone,
  desdeCoordinacion,
  readOnly,
  onSaveRef,
  coordenadas
}) {
  const [formData, setFormData] = useState({});
  const [tipoVisitasOptions, setTipoVisitasOptions] = useState([]);
  const [comboInterlocutorOptions, setComboInterlocutorOptions] = useState([]);

  const { visita, setVisita } = useVisita();

  const { data: dataComboVisitaValorTipo } = useQuery(GET_COMBO_VISITA_VALOR, {
    variables: { idComboVisita: ID_COMBO_TIPO_VISITA }
  });

  useEffect(() => {
    if (!dataComboVisitaValorTipo) return;
    setTipoVisitasOptions(adaptCombosVisitas(dataComboVisitaValorTipo));
  }, [dataComboVisitaValorTipo]);

  const [loadComboInterlocutor, { data: dataComboVisitaValorInter }] = useLazyQuery(GET_COMBO_VISITA_VALOR, {
    fetchPolicy: "no-cache"
  });

  useEffect(() => {
    if (!dataComboVisitaValorInter) return;
    setComboInterlocutorOptions(adaptCombosVisitas(dataComboVisitaValorInter));
  }, [dataComboVisitaValorInter]);

  const [getInformacionGeneral, { data: dataInformacionGeneral }] = useLazyQuery(GET_INFORMACION_GENERAL, {
    fetchPolicy: 'no-cache',
    skip: !idEvaluacion || !idProfesional
  });

  useEffect(() => {
    if (!dataInformacionGeneral) return;
    if (dataInformacionGeneral?.informacionGeneralVisita?.success) {
      setVisita((prev) => ({
        ...prev,
        ...parseInformacionGeneral(dataInformacionGeneral.informacionGeneralVisita)
      }));
    }
  }, [dataInformacionGeneral]);

  const [save] = useMutation(SAVE_VISITA_OTROS_PROFESIONALES);

  const isOtroProfesional = useMemo(() => {

    return idTipoProfesional == idTiposProfesionales.KINESIOLOGO ||
      idTipoProfesional == idTiposProfesionales.PSICOLOGO ||
      idTipoProfesional == idTiposProfesionales.PSIQUIATRA ||
      idTipoProfesional == idTiposProfesionales.TERAPISTA_OCUPACIONAL ||
      idTipoProfesional == idTiposProfesionales.ASISTENTE_SOCIAL ||
      idTipoProfesional == idTiposProfesionales.REHABILITADOR;
  }, [idTipoProfesional]);

  const formattedInputs = useMemo(() => {
    const isPresencial = formData?.idComboVisitaValorTipoVisita === ID_TIPO_VISITA_PRESENCIAL;
    const interlocutorPacienteId = comboInterlocutorOptions?.find((x) => x.displayName.toLowerCase().includes('paciente'))?.id;

    return inputs.map((input) => {
      const newInput = { ...input };

      switch (input.name) {
        case "fechaVisita":
          newInput.hidden = !isOtroProfesional;
          const hoy = new Date();
          newInput.minDate = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate() - 2);
          newInput.maxDate = hoy;
          newInput.defaultValue = hoy.toISOString().split("T")[0];
          newInput.disabled = !!formData?.idVisita || !!isClone ? true : newInput.disabled;
          break;
        case 'idComboVisitaValorTipoVisita':
          newInput.hidden = !isOtroProfesional;
          break;
        case 'idComboVisitaValorInterlocutor':
          newInput.hidden = !isPresencial || !isOtroProfesional;
          newInput.options = comboInterlocutorOptions;
          break;
        case 'firmaPaciente':
          newInput.hidden = !isPresencial || !isOtroProfesional;
          break;
        case 'email':
          newInput.hidden = !isPresencial || !isOtroProfesional;
          break;
        case 'nombreCompleto':
          newInput.hidden = !isPresencial || !isOtroProfesional;
          newInput.disabled = isPresencial && formData?.idComboVisitaValorInterlocutor === interlocutorPacienteId;
          break;
        default:
          break;
      }

      if (readOnly) newInput.disabled = true;
      return newInput;
    });
  }, [
    readOnly,
    formData?.idComboVisitaValorTipoVisita,
    formData?.idComboVisitaValorInterlocutor,
    desdeCoordinacion,
    formData?.fechaVisita,
    formData?.idVisita,
    comboInterlocutorOptions,
    isOtroProfesional]
  );

  useStepSave(onSaveRef, async (validate) => {
    if (validate) validateStepFields({ formattedInputs, formData });

    const coordenadas = await obtenerCoordenadas();
    const visitaConCoordenadas = {
      ...formData,
      desdeCoordinacion: !!desdeCoordinacion,
      posX: coordenadas?.longitud?.toString(),
      posY: coordenadas?.latitud?.toString()
    };

    const { data } = await save({ variables: { ...adaptAddVisitaOtrosProfesionalesInput(visitaConCoordenadas) } });
    const { error } = data?.saveVisitaOtrosProfesionales || {};

    if (!!error) {
      toast.error(error);
      const errorSave = new Error(error);
      errorSave.isCustomError = true;
      throw errorSave;
    }
    const idVisita = data?.saveVisitaOtrosProfesionales?.valorEsperado?.idVisita;
    if (!idVisita) throw new Error("Error al guardar");
    setVisita((prev) => ({ ...prev, ...formData, idVisita }));

  });

  useEffect(() => {
    if (!Array.isArray(combosVisita) || combosVisita.length === 0) return;

    const interlocutorId = combosVisita.find((c) => c.comboName === COMBO_VISITA_INERLOCUTOR)?.id;

    if (Number.isInteger(interlocutorId)) {
      loadComboInterlocutor({ variables: { idComboVisita: interlocutorId } });
    }
  }, [combosVisita, loadComboInterlocutor]);


  useEffect(() => {
    if (!idEvaluacion || !idProfesional) return;
    const idFrecuenciaValor = idFrecuencia || 0;
    getInformacionGeneral({ variables: { idEvaluacion, idProfesional, idFrecuencia: idFrecuenciaValor, idVisita: visita?.idVisita } });
  }, [idEvaluacion, idPaciente, visita?.idVisita, idFrecuencia]);

  useEffect(() => {
    if (visita && !formData.idVisita) {
      const inputNames = getInputNames(inputs);
      setFormData({
        ...filterVisitaData(visita, inputNames),
        ...coordenadas,
        idVisita: visita.idVisita,
        idEvaluacion: visita.idEvaluacion,
        idProfesional: visita.idProfesional,
        idFrecuencia: visita.idFrecuencia,
        idTipoProfesional: idTipoProfesional,
        idFiguraProfesional: visita.idFiguraProfesional
      });
    }
  }, [visita, coordenadas]);


  useEffect(() => {
    const interlocutorPacienteId = comboInterlocutorOptions?.find((x) => x.displayName.toLowerCase().includes('paciente'))?.id;
    if (!!formData.idComboVisitaValorInterlocutor && !!interlocutorPacienteId && formData.idComboVisitaValorInterlocutor === interlocutorPacienteId) {
      handleChange('nombreCompleto', formData?.paciente || visita?.paciente);
    }
  }, [formData.idComboVisitaValorInterlocutor, comboInterlocutorOptions, formData?.paciente || visita?.paciente]);

  const ensureIdVisita = async () => {
    if (Number(formData?.idVisita) > 0) return formData?.idVisita;
    try {
      const { data } = await save({
        variables: { ...adaptAddVisitaOtrosProfesionalesInput({ ...formData }) }
      });

      const { error } = data?.saveVisitaOtrosProfesionales || {};

      if (!!error) {
        toast.error(error);
        const errorSave = new Error(error);
        errorSave.isCustomError = true;
        throw errorSave;
      }

      const idVisitaNuevo = data?.saveVisitaOtrosProfesionales?.valorEsperado?.idVisita;

      if (Number(idVisitaNuevo) > 0) {
        setFormData((prev) => ({ ...prev, idVisita: idVisitaNuevo }));
        return idVisitaNuevo;
      }
      return null;
    } catch (e) {
      return null;
    }
  };


  const handleChange = (name, value) => {
    const interlocutorPacienteId = comboInterlocutorOptions?.find((x) => x.displayName.toLowerCase().includes('paciente'))?.id;

    if (name === 'idComboVisitaValorInterlocutor' && value !== interlocutorPacienteId) {
      setFormData((prv) => ({
        ...prv,
        [name]: value,
        nombreCompleto: ''
      }));
    }
    else {
      setFormData((prv) => ({ ...prv, [name]: value }));
    }
  };

  return (
    <CardBox>
      <Grid container spacing={2}>
        {formattedInputs.
          filter((input) => !input.hidden).
          map((input) => {
            if (input.name === 'idComboVisitaValorTipoVisita' && !!tipoVisitasOptions?.length) {
              input.options = tipoVisitasOptions;
            }
            return (
              <Grid size={{ xs: 12 }} key={input.name}>
                {input.name === 'firmaPaciente' && <VisitaFirmaInput
                  key={'firmaPaciente' + input.name}
                  idVisita={formData.idVisita || visita.idVisita}
                  disableChange={formData?.firmaPaciente?.idArchivo}
                  ensureIdVisita={ensureIdVisita}
                  handleChange={handleChange}
                  inputName={input.name}
                  readOnly={readOnly} />
                }
                {input.name !== 'firmaPaciente' && <RenderInputByTypeMobile
                  key={input.name}
                  input={input}
                  value={formData[input.name]}
                  handleChange={handleChange} />
                }
              </Grid>);

          })}
      </Grid>
    </CardBox>);

}

const inputs = [
  {
    name: 'paciente',
    displayName: 'Paciente',
    type: 'infoRow'
  },
  {
    name: 'idPaciente',
    displayName: 'HC',
    type: 'infoRow'
  },
  {
    name: 'fechaNacimiento',
    displayName: 'Fecha Nac',
    type: 'infoRow'
  },
  {
    name: 'edad',
    displayName: 'Edad',
    type: 'infoRow'
  },
  {
    name: 'direccion',
    displayName: 'Dirección',
    type: 'infoRow'
  },
  {
    name: 'observacionAsistencial',
    displayName: 'Obs. Asistencial',
    type: 'infoRow'
  },
  {
    name: 'telefono',
    displayName: 'Teléfono',
    type: 'infoRow'
  },
  {
    name: 'telefonFamiliar',
    displayName: 'Teléfono Familiar',
    type: 'infoRow'
  },
  {
    displayName: "Fecha y Hora de ingreso",
    name: "fechaVisita",
    type: "datetime",
    required: true,
    disableFuture: true
  },
  {
    displayName: "Tipo de visita",
    name: "idComboVisitaValorTipoVisita",
    type: "select",
    required: true
  },
  {
    name: "idComboVisitaValorInterlocutor",
    displayName: "Interlocutor",
    type: "select",
    required: true
  },
  {
    name: "nombreCompleto",
    displayName: "Nombre",
    type: "string",
    maxLength: 250,
    required: true
  },
  {
    name: "firmaPaciente",
    displayName: "Firma Paciente / Familiar",
    type: "signature"
  },
  {
    name: "email",
    displayName: "Email paciente / Interlocutor",
    type: "string",
    maxLength: 250,
    required: false
  },
  {
    name: "alergias",
    displayName: "Alergias",
    type: "infoRow"
  }];